import React, { useEffect } from 'react'
import { Route, Redirect } from 'react-router-dom'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import { Loading } from 'components/UI'
import { setLoggedIn } from 'store/actions/auth'
import { openModal } from 'store/actions/modals'
import { checkIfResourcesRefreshIsNeeded } from 'store/actions/resources'
import { checkIfSettingsRefreshIsNeeded } from 'store/actions/settings'
import { checkIfUsersRefreshIsNeeded } from 'store/actions/users'
import useRedirectTo from 'components/Hooks/useRedirectTo'

// Interceptor to change the route if the user is not authentificated
export const PrivateRoute = ({
  checkIfResourcesRefreshIsNeeded,
  checkIfSettingsRefreshIsNeeded,
  checkIfUsersRefreshIsNeeded,
  component: Component,
  isAuthenticated,
  isLoadingUserInfo,
  isLoadingResources,
  isLoadingSettings,
  isInitiatingPush,
  isLoadingShiftsToConfirm,
  isLoadingAttributeReminderToConfirm,
  isPunchToken,
  openModal,
  pushInitialized,
  resourcesAllLoaded,
  restrictToPunchTerminal,
  setLoggedIn,
  settingsAllLoaded,
  shiftsToConfirm,
  attributeReminderToConfirm,
  shiftsToConfirmInitialized,
  attributeReminderToConfirmInitialized,
  userInfo,
  location,
  history,
  ...rest
}) => {
  const redirectToLoginPath = useRedirectTo()

  useEffect(() => {
    // If the user is not authenticated, check if there is a token in local storage
    if (!isAuthenticated) {
      const token = localStorage.getItem('token')

      // If no token, redirect him to the login page
      if (token) setLoggedIn(token)
    }
  }, [isAuthenticated, setLoggedIn])

  useEffect(() => {
    // If the user has some shifts to confirm, open the modal to confirm them
    if (shiftsToConfirm) openModal('EmployeeShiftConfirmationModal', shiftsToConfirm)
  }, [shiftsToConfirm, openModal])

  useEffect(() => {
    // If the user has an attribute expiration reminder to confirm, open the modal to confirm it
    if (attributeReminderToConfirm) openModal('EmployeeAttributeExpirationConfirmationModal')
  }, [attributeReminderToConfirm, openModal])

  const token = localStorage.getItem('token')
  const redirectPath = redirectToLoginPath ? `/login?redirectTo=${redirectToLoginPath}` : '/login'

  if (!token) return <Redirect to={redirectPath} />

  if (isPunchToken && (!resourcesAllLoaded || !settingsAllLoaded || !pushInitialized)) return <Loading />
  if (
    !isPunchToken &&
    (!userInfo || !resourcesAllLoaded || !settingsAllLoaded || !pushInitialized || !shiftsToConfirmInitialized || !shiftsToConfirmInitialized)
  )
    return <Loading />

  // On every page render, check if the refresh is necessary
  checkIfResourcesRefreshIsNeeded()
  if (!isPunchToken) checkIfUsersRefreshIsNeeded()
  checkIfSettingsRefreshIsNeeded()

  const isInPunchSection = window.location.pathname.startsWith('/punch') && window.location.pathname !== '/punchLocation'
  // If a punch token, limit to the punch section
  if (isPunchToken && !isInPunchSection) return <Redirect to="/punch/auth" />
  // We whould have a way to redirect the user to a right default page (manager --> their home, employee --> my/home)
  if (!isPunchToken && isInPunchSection) return <Redirect to="/my/home" />
  // Trigger selection of the location, then start a punch terminal
  if (!isPunchToken && restrictToPunchTerminal) return <Redirect to="/punchLocation" />
  // Custom validation to display the Component (could return a custom component or a Redirect to an other page)
  if (rest.validatePermission && rest.validatePermission()) return rest.validatePermission()

  // If the user is authenticated, return the component he wants to see
  return <Route {...rest} render={props => (isAuthenticated ? <Component {...props} /> : <Redirect to={redirectToLoginPath} />)} />
}

const mapStateToProps = ({ applicationInfo, users, auth, notifications, punchTerminal, resources, settings }) => ({
  userInfo: users.userInfo,
  isAuthenticated: auth.isAuthenticated,
  isLoadingUserInfo: users.isLoadingUserInfo,
  isLoadingResources: resources.isLoadingResources,
  isLoadingSettings: settings.isLoadingSettings,
  isLoadingShiftsToConfirm: applicationInfo.isLoadingShiftsToConfirm,
  isLoadingAttributeReminderToConfirm: applicationInfo.isLoadingAttributeReminderToConfirm,
  isPunchToken: punchTerminal.isPunchToken,
  resourcesAllLoaded: resources.resourcesAllLoaded,
  restrictToPunchTerminal: auth.restrictToPunchTerminal,
  settingsAllLoaded: settings.settingsAllLoaded,
  isInitiatingPush: notifications.isInitiatingPush,
  pushInitialized: notifications.pushInitialized,
  shiftsToConfirm: applicationInfo.shiftsToConfirm,
  attributeReminderToConfirm: applicationInfo.attributeReminderToConfirm,
  shiftsToConfirmInitialized: applicationInfo.shiftsToConfirmInitialized,
  attributeReminderToConfirmInitialized: applicationInfo.attributeReminderToConfirmInitialized
})

const mapDispatchToProps = dispatch => ({
  setLoggedIn: token => dispatch(setLoggedIn(token)),
  checkIfResourcesRefreshIsNeeded: () => dispatch(checkIfResourcesRefreshIsNeeded()),
  checkIfSettingsRefreshIsNeeded: () => dispatch(checkIfSettingsRefreshIsNeeded()),
  checkIfUsersRefreshIsNeeded: () => dispatch(checkIfUsersRefreshIsNeeded()),
  openModal: (...props) => dispatch(openModal(...props))
})

export default connect(mapStateToProps, mapDispatchToProps)(PrivateRoute)

// Set the proptypes for validation
PrivateRoute.propTypes = {
  path: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  component: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired
}
